/**
 * Create PostHog Support tickets from our own UI.
 *
 * Uses `posthog.conversations`, the documented API for custom support surfaces
 * (https://posthog.com/docs/support/javascript-api). It is the same channel the support
 * widget uses, so each ticket arrives with the sender's person, session replay and current
 * URL attached, and a logged-in user's ticket is tied to their verified identity.
 *
 * This is deliberately a browser call, not one proxied through our API. PostHog only accepts
 * widget tickets from the origins listed under Support → Settings → Allowed domains, which a
 * server-to-server call does not have. The consequence: tickets can only be created from a
 * hostname on that list (prod today), and anywhere else `createSupportTicket` reports
 * `request_failed`.
 */
import type { ConversationsUnavailableReason } from "posthog-js";

import logger from "./logger";
import posthog from "./posthog";

export interface SupportMessage {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export type SupportFailureReason =
  ConversationsUnavailableReason | "request_failed";

export type SupportTicketResult =
  { ok: true; ticketId: string } | { ok: false; reason: SupportFailureReason };

interface Options {
  /** How long to wait for the conversations module, which loads after posthog-js itself. */
  timeoutMs?: number;
  pollMs?: number;
}

function unavailableReason(): SupportFailureReason | null {
  const conversations = posthog.conversations;
  if (!conversations) return "not_loaded";
  if (conversations.isAvailable()) return null;
  return conversations.getUnavailableReason() ?? "not_loaded";
}

/**
 * The conversations module is fetched lazily once remote config says Support is on, so a
 * visitor who submits quickly can beat it. Wait for it rather than fail them.
 */
async function waitUntilAvailable(
  timeoutMs: number,
  pollMs: number
): Promise<SupportFailureReason | null> {
  const deadline = Date.now() + timeoutMs;
  let reason = unavailableReason();
  while (reason !== null && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, pollMs));
    reason = unavailableReason();
  }
  return reason;
}

/**
 * Open a new support ticket. Never throws: callers get a result they can show the visitor,
 * because a contact form that fails silently loses the message.
 */
export async function createSupportTicket(
  { name, email, subject, message }: SupportMessage,
  { timeoutMs = 5000, pollMs = 250 }: Options = {}
): Promise<SupportTicketResult> {
  const reason = await waitUntilAvailable(timeoutMs, pollMs);
  if (reason !== null) {
    logger.warn("support ticket not sent: conversations unavailable", {
      reason,
    });
    return { ok: false, reason };
  }

  const body = [subject.trim(), message.trim()].filter(Boolean).join("\n\n");
  try {
    // `true` forces a new ticket: each submission is its own conversation rather than a
    // reply appended to whatever ticket this browser last had open.
    const response = await posthog.conversations.sendMessage(
      body,
      { name: name.trim(), email: email.trim() },
      true
    );
    if (!response) {
      return { ok: false, reason: "not_loaded" };
    }
    return { ok: true, ticketId: response.ticket_id };
  } catch (error) {
    // Name and message only — never the form contents.
    logger.exception("support ticket request failed", error);
    return { ok: false, reason: "request_failed" };
  }
}
